import React from 'react';
import { CreditCard, Download, ChevronRight } from 'lucide-react';
import './WorkyMainDemoOpsPayrollScreen.css';

// ── Types ────────────────────────────────────────────────────────────────────

interface PayrollCycle {
    readonly id: string;
    readonly period: string;
    readonly stubCount: number;
    readonly totalDue: string;
}

interface PayStubEntry {
    readonly id: string;
    readonly name: string;
    readonly initials: string;
    readonly avatarBg: string;
    readonly role: string;
    readonly hours: number;
    readonly rateDisplay: string;
    readonly gross: string;
    readonly deductionTotal: string;
    readonly deductionDetail: string;
    readonly net: string;
    readonly isHighlighted: boolean;
}

// ── Static data ──────────────────────────────────────────────────────────────

const PAYROLL_CYCLES: readonly PayrollCycle[] = [
    { id: 'apr1',  period: 'Apr 1 – Apr 15',  stubCount: 44, totalDue: '$184,302' },
    { id: 'mar16', period: 'Mar 16 – Mar 31',  stubCount: 81, totalDue: '$276,450' },
    { id: 'mar1',  period: 'Mar 1 – Mar 15',   stubCount: 77, totalDue: '$262,890' },
    { id: 'feb16', period: 'Feb 16 – Feb 28',  stubCount: 76, totalDue: '$259,340' },
    { id: 'feb1',  period: 'Feb 1 – Feb 15',   stubCount: 73, totalDue: '$249,500' },
    { id: 'jan16', period: 'Jan 16 – Jan 31',  stubCount: 70, totalDue: '$238,900' },
    { id: 'jan1',  period: 'Jan 1 – Jan 15',   stubCount: 36, totalDue: '$122,760' },
];

// Pay stubs for the Apr 1–Apr 15 cycle (expanded in animation).
// David Chen's row reflects his $85/hr rate + deductions set in Phase 3.
const APR1_STUBS: readonly PayStubEntry[] = [
    {
        id: 'priya',
        name: 'Priya Patel',
        initials: 'PP',
        avatarBg: 'linear-gradient(135deg, #6d28d9, #a78bfa)',
        role: 'PSW',
        hours: 64,
        rateDisplay: '$34/hr',
        gross: '$2,176',
        deductionTotal: '–',
        deductionDetail: '',
        net: '$2,176',
        isHighlighted: false,
    },
    {
        id: 'david',
        name: 'David Chen',
        initials: 'DC',
        avatarBg: 'linear-gradient(135deg, #0e7490, #22d3ee)',
        role: 'PT',
        hours: 80,
        rateDisplay: '$85/hr',
        gross: '$6,800',
        deductionTotal: '–$370',
        deductionDetail: '',
        net: '$6,430',
        isHighlighted: true,
    },
    {
        id: 'sarah',
        name: 'Sarah Mitchell',
        initials: 'SM',
        avatarBg: 'linear-gradient(135deg, #1d4ed8, #60a5fa)',
        role: 'RN',
        hours: 72,
        rateDisplay: '$87/hr',
        gross: '$6,264',
        deductionTotal: '–',
        deductionDetail: '',
        net: '$6,264',
        isHighlighted: false,
    },
    {
        id: 'james',
        name: 'James Okafor',
        initials: 'JO',
        avatarBg: 'linear-gradient(135deg, #065f46, #34d399)',
        role: 'LPN',
        hours: 80,
        rateDisplay: '$42/hr',
        gross: '$3,360',
        deductionTotal: '–',
        deductionDetail: '',
        net: '$3,360',
        isHighlighted: false,
    },
    {
        id: 'maria',
        name: 'Maria Santos',
        initials: 'MS',
        avatarBg: 'linear-gradient(135deg, #9f1239, #fb7185)',
        role: 'RN',
        hours: 84,
        rateDisplay: '$91/hr',
        gross: '$7,644',
        deductionTotal: '–',
        deductionDetail: '',
        net: '$7,644',
        isHighlighted: false,
    },
];

// ── Props ────────────────────────────────────────────────────────────────────

interface WorkyMainDemoOpsPayrollScreenProps {
    readonly isFirstCycleExpanded: boolean;
}

// ── Component ────────────────────────────────────────────────────────────────

const WorkyMainDemoOpsPayrollScreen = ({
    isFirstCycleExpanded,
}: WorkyMainDemoOpsPayrollScreenProps): React.ReactElement => (
    <div className='wpy-container'>

        {/* ── Header ──────────────────────────────────────────────── */}
        <div className='wpy-header'>
            <div className='wpy-header-top'>
                <div className='wpy-title-group'>
                    <div className='wpy-logo-row'>
                        <div className='wpy-logo-icon'>
                            <CreditCard size={12} />
                        </div>
                        <span className='wpy-title'>Worky Pay</span>
                    </div>
                    <span className='wpy-subtitle'>Pay your employees and manage pay stubs</span>
                </div>
                <div className='wpy-header-actions'>
                    <button className='wpy-header-btn wpy-header-btn--ghost'>
                        <Download size={9} />
                        Export All
                    </button>
                    <button className='wpy-header-btn wpy-header-btn--primary'>
                        Run Payroll
                    </button>
                </div>
            </div>

            {/* Stat cards */}
            <div className='wpy-stats'>
                <div className='wpy-stat'>
                    <span className='wpy-stat-label'>YTD Paid</span>
                    <span className='wpy-stat-value'>$1.41M</span>
                    <span className='wpy-stat-sub'>↑ 18% vs last year</span>
                </div>
                <div className='wpy-stat'>
                    <span className='wpy-stat-label'>Current Period</span>
                    <span className='wpy-stat-value'>$184K</span>
                    <span className='wpy-stat-sub'>Apr 1 – Apr 15</span>
                </div>
                <div className='wpy-stat'>
                    <span className='wpy-stat-label'>Pay Stubs</span>
                    <span className='wpy-stat-value'>44</span>
                    <span className='wpy-stat-sub'>this period</span>
                </div>
                <div className='wpy-stat'>
                    <span className='wpy-stat-label'>On Time</span>
                    <span className='wpy-stat-value'>100%</span>
                    <span className='wpy-stat-sub'>all cycles</span>
                </div>
            </div>
        </div>

        {/* ── Table area ──────────────────────────────────────────── */}
        <div className='wpy-table-area'>

            <div className='wpy-table-toolbar'>
                <span className='wpy-table-title'>All Payrolls</span>
                <button className='wpy-export-btn'>
                    <Download size={9} /> Export
                    <ChevronRight size={9} />
                </button>
            </div>

            <div className='wpy-col-header'>
                <span className='wpy-col-th'>Pay Cycle</span>
                <span className='wpy-col-th' style={{ textAlign: 'center' }}>#Pay Stubs</span>
                <span className='wpy-col-th wpy-col-th--right'>Total Due($)</span>
                <span className='wpy-col-th' />
            </div>

            {PAYROLL_CYCLES.map((cycle: PayrollCycle, idx: number) => {
                const isExpanded: boolean = idx === 0 && isFirstCycleExpanded;
                return (
                    <React.Fragment key={cycle.id}>
                        <div
                            className={`wpy-cycle-row${isExpanded ? ' wpy-cycle-row--active' : ''}`}
                            {...(idx === 0 ? { 'data-demo-target': 'payroll-cycle-apr1' } : {})}
                        >
                            <span className='wpy-cycle-period'>
                                <ChevronRight
                                    size={11}
                                    className={`wpy-cycle-chevron${isExpanded ? ' wpy-cycle-chevron--open' : ''}`}
                                />
                                {cycle.period}
                            </span>
                            <span className='wpy-cycle-stubs'>{cycle.stubCount}</span>
                            <span className='wpy-cycle-total'>{cycle.totalDue}</span>
                            <span />
                        </div>

                        {/* Expanded pay stubs sub-table */}
                        {isExpanded && (
                            <div className='wpy-expanded'>
                                <div className='wpy-stub-header'>
                                    <span className='wpy-stub-th'>Worker</span>
                                    <span className='wpy-stub-th' style={{ textAlign: 'right' }}>Hrs</span>
                                    <span className='wpy-stub-th' style={{ textAlign: 'right' }}>Rate</span>
                                    <span className='wpy-stub-th' style={{ textAlign: 'right' }}>Gross</span>
                                    <span className='wpy-stub-th' style={{ textAlign: 'right' }}>Deductions</span>
                                    <span className='wpy-stub-th' style={{ textAlign: 'right' }}>Net Pay</span>
                                </div>

                                {APR1_STUBS.map((stub: PayStubEntry) => (
                                    <div
                                        key={stub.id}
                                        className={`wpy-stub-row${stub.isHighlighted ? ' wpy-stub-row--highlight' : ''}`}
                                        {...(stub.isHighlighted ? { 'data-demo-target': 'payroll-david-row' } : {})}
                                    >
                                        <div className='wpy-stub-worker'>
                                            <div
                                                className='wpy-stub-avatar'
                                                style={{ background: stub.avatarBg }}
                                            >
                                                {stub.initials}
                                            </div>
                                            <div>
                                                <span className='wpy-stub-name'>
                                                    {stub.name}
                                                    {stub.isHighlighted && (
                                                        <span className='wpy-new-badge'>★ Updated</span>
                                                    )}
                                                </span>
                                                <span className='wpy-stub-role'>{stub.role}</span>
                                            </div>
                                        </div>

                                        <span className='wpy-stub-cell'>{stub.hours}h</span>
                                        <span className='wpy-stub-cell'>{stub.rateDisplay}</span>
                                        <span className='wpy-stub-cell wpy-stub-cell--bold'>{stub.gross}</span>

                                        {stub.isHighlighted ? (
                                            <div className='wpy-stub-cell--deduction'>
                                                <span className='wpy-deduction-total'>{stub.deductionTotal}</span>
                                                <span className='wpy-deduction-detail'>{stub.deductionDetail}</span>
                                            </div>
                                        ) : (
                                            <span className='wpy-stub-cell wpy-stub-cell--muted'>{stub.deductionTotal}</span>
                                        )}

                                        <span className={stub.isHighlighted ? 'wpy-stub-cell--net-highlight' : 'wpy-stub-cell wpy-stub-cell--bold'}>
                                            {stub.net}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </React.Fragment>
                );
            })}

        </div>
    </div>
);

export { WorkyMainDemoOpsPayrollScreen };
export type { WorkyMainDemoOpsPayrollScreenProps };
